import { cn } from "@/lib/utils";

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: "none" | "sm" | "md" | "lg";
  variant?: "default" | "elevated" | "bordered" | "glass" | "gradient";
  hover?: boolean;
  animate?: boolean;
}

export function Card({
  children,
  className,
  padding = "md",
  variant = "default",
  hover = false,
  animate = false,
}: CardProps) {
  const paddings = {
    none: "",
    sm: "p-4",
    md: "p-6",
    lg: "p-8",
  };

  const variants = {
    default: "bg-white border border-gray-100 shadow-card",
    elevated: "bg-white shadow-card-hover",
    bordered: "bg-white border-2 border-gray-200",
    glass: "bg-white/70 backdrop-blur-xl border border-white/20 shadow-card",
    gradient:
      "bg-gradient-to-br from-white to-gray-50 border border-gray-100 shadow-card",
  };

  return (
    <div
      className={cn(
        // Base
        "rounded-2xl",
        // Variant
        variants[variant],
        // Padding
        paddings[padding],
        // Hover effect
        hover &&
          "transition-all duration-300 hover:shadow-card-hover hover:-translate-y-0.5 cursor-pointer",
        // Animate
        animate && "animate-fade-in-up",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}

export function CardHeader({ children, className, action }: CardHeaderProps) {
  return (
    <div
      className={cn("flex items-start justify-between gap-4 mb-4", className)}
    >
      <div className="flex-1">{children}</div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
  as?: "h1" | "h2" | "h3" | "h4";
}

export function CardTitle({
  children,
  className,
  as: Component = "h3",
}: CardTitleProps) {
  return (
    <Component
      className={cn(
        "text-lg font-semibold text-gray-900 tracking-tight",
        className,
      )}
    >
      {children}
    </Component>
  );
}

interface CardDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export function CardDescription({ children, className }: CardDescriptionProps) {
  return (
    <p className={cn("text-sm text-gray-500 mt-1", className)}>{children}</p>
  );
}

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
}

export function CardContent({
  children,
  className,
  title,
  style,
}: CardContentProps) {
  return (
    <div className={className} title={title} style={style}>
      {children}
    </div>
  );
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
  border?: boolean;
}

export function CardFooter({
  children,
  className,
  border = true,
}: CardFooterProps) {
  return (
    <div
      className={cn(
        "mt-4 pt-4",
        border && "border-t border-gray-100",
        className,
      )}
    >
      {children}
    </div>
  );
}

// Nuevo: StatCard para métricas
interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: "primary" | "success" | "warning" | "danger" | "purple" | "blue";
  className?: string;
}

export function StatCard({
  title,
  value,
  icon,
  trend,
  color = "primary",
  className,
}: StatCardProps) {
  const colors = {
    primary: {
      bg: "bg-primary-50",
      icon: "text-primary-600",
      accent: "from-primary-500/20",
    },
    success: {
      bg: "bg-emerald-50",
      icon: "text-emerald-600",
      accent: "from-emerald-500/20",
    },
    warning: {
      bg: "bg-amber-50",
      icon: "text-amber-600",
      accent: "from-amber-500/20",
    },
    danger: {
      bg: "bg-red-50",
      icon: "text-red-600",
      accent: "from-red-500/20",
    },
    purple: {
      bg: "bg-purple-50",
      icon: "text-purple-600",
      accent: "from-purple-500/20",
    },
    blue: {
      bg: "bg-blue-50",
      icon: "text-blue-600",
      accent: "from-blue-500/20",
    },
  };

  return (
    <Card
      className={cn("relative overflow-hidden", className)}
      padding="md"
      hover
    >
      {/* Decorative gradient */}
      <div
        className={cn(
          "absolute -top-12 -right-12 w-24 h-24 rounded-full bg-gradient-to-br to-transparent opacity-50",
          colors[color].accent,
        )}
      />

      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1 tracking-tight">
            {value}
          </p>
          {trend && (
            <div
              className={cn(
                "flex items-center gap-1 mt-2 text-xs font-medium",
                trend.isPositive ? "text-emerald-600" : "text-red-600",
              )}
            >
              <span>{trend.isPositive ? "↑" : "↓"}</span>
              <span>{Math.abs(trend.value)}%</span>
              <span className="text-gray-400 font-normal">vs mes anterior</span>
            </div>
          )}
        </div>
        {icon && (
          <div
            className={cn(
              "w-11 h-11 rounded-xl flex items-center justify-center",
              colors[color].bg,
            )}
          >
            <span className={colors[color].icon}>{icon}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

export default Card;
